import React, { useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import {
  Typography,
  Button,
  TextField,
  Grid,
  Container,
  Paper,
  Box,
  Divider
} from '@mui/material';
import { API } from '../../config';
import { getApiErrorMessage } from '../../utils/apiError';

const isValidEmail = (value) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

const CustomerSignIn = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const history = useHistory();

  const handleSignIn = async (e) => {
    e.preventDefault();

    const normalizedEmail = email.trim().toLowerCase();
    const normalizedPassword = password.trim();

    if (!isValidEmail(normalizedEmail)) {
      alert('Please enter a valid email address.');
      return;
    }

    if (!normalizedPassword) {
      alert('Please enter your password.');
      return;
    }

    try {
      const res = await fetch(`${API}/api/auth/login-customer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: normalizedEmail,
          password: normalizedPassword,
        })
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        alert(getApiErrorMessage(data, 'Invalid email or password.'));
        return;
      }

      if (!data.token) {
        alert('ERROR: Backend did not return a token. Cannot sign in.');
        return;
      }

      localStorage.setItem('token', data.token);
      localStorage.setItem('customer_token', data.token);
      localStorage.setItem('userType', 'customer');
      localStorage.setItem('role', 'customer');

      history.push('/customer/dashboard');
    } catch (err) {
      console.error('login error', err);
      alert('Network error. Please try again.');
    }
  };

    const handleForgotPassword = () => {
    const resetEmail = email.trim().toLowerCase();
    const emailQuery = resetEmail
      ? `&email=${encodeURIComponent(resetEmail)}`
      : '';

    history.push(`/forgot-password?role=customer${emailQuery}`);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f8fafc',
        py: { xs: 4, md: 8 },
      }}
    >
      <Container
        maxWidth="sm"
        sx={{
          px: { xs: 2.25, sm: 3 },
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: { xs: 2.5, sm: 3.5, md: 5 },
            borderRadius: 4,
            border: '1px solid rgba(15, 23, 42, 0.08)',
            maxWidth: 620,
            mx: 'auto',
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <Box
            sx={{
              textAlign: 'center',
              mb: 3,
              maxWidth: 540,
              mx: 'auto',
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontSize: { xs: '1.95rem', sm: '2.2rem', md: '2.35rem' },
                fontWeight: 800,
                lineHeight: 1.15,
                letterSpacing: '-0.035em',
                color: '#0f172a',
                mb: 1,
              }}
            >
              Customer Sign In
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: '#64748b',
                maxWidth: 500,
                mx: 'auto',
                lineHeight: 1.55,
                mb: 3,
                fontSize: { xs: '0.98rem', sm: '1rem' },
              }}
            >
              Sign in to post loads, track updates, manage payments, and communicate with drivers or carriers.
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 1.25,
                maxWidth: 430,
                mx: 'auto',
                mb: 2,
              }}
            >
              <Button
                component={Link}
                to="/customer/sign-in"
                variant="contained"
                color="primary"
                fullWidth
                sx={{
                  minHeight: 44,
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  borderRadius: 2,
                  whiteSpace: 'nowrap',
                  fontSize: { xs: '0.88rem', sm: '0.86rem', md: '0.9rem' },
                  letterSpacing: '0.025em',
                }}
              >
                SIGN IN
              </Button>

              <Button
                component={Link}
                to="/customer/sign-up"
                variant="outlined"
                color="primary"
                fullWidth
                sx={{
                  minHeight: 44,
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  borderRadius: 2,
                  whiteSpace: 'nowrap',
                  fontSize: { xs: '0.88rem', sm: '0.86rem', md: '0.9rem' },
                  letterSpacing: '0.025em',
                }}
              >
                CREATE ACCOUNT
              </Button>
            </Box>

            <Button
              component={Link}
              to="/"
              variant="text"
              sx={{
                textTransform: 'uppercase',
                fontWeight: 800,
                fontSize: '0.82rem',
                letterSpacing: '0.04em',
                mb: 2,
              }}
            >
              BACK TO HOME
            </Button>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <form onSubmit={handleSignIn} noValidate>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Email"
                  type="text"
                  variant="outlined"
                  fullWidth
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  inputProps={{
                    inputMode: 'email',
                    autoCapitalize: 'none',
                    spellCheck: false,
                  }}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Password"
                  type="password"
                  variant="outlined"
                  fullWidth
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  fullWidth
                  sx={{
                    mt: 1,
                    minHeight: 46,
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    borderRadius: 2,
                    letterSpacing: '0.025em',
                  }}
                >
                  SIGN IN
                </Button>
              </Grid>

              <Grid item xs={12} sx={{ textAlign: 'center' }}>
                <Button
                  variant="text"
                  color="primary"
                  onClick={handleForgotPassword}
                  sx={{
                    textTransform: 'uppercase',
                    fontWeight: 800,
                    fontSize: '0.82rem',
                    letterSpacing: '0.04em',
                  }}
                >
                  FORGOT PASSWORD?
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Container>
    </Box>
  );
};

export default CustomerSignIn;